import React, { useState, useEffect, useRef } from 'react';
import {
  Shield,
  Building2,
  Lock,
  Send,
  Paperclip,
  CheckCircle2,
  AlertTriangle,
  FileSpreadsheet,
  FileText,
  RefreshCw,
  GitGraph,
  Download,
  ArrowLeft,
  Users,
  Search,
  Check,
  FileCheck2,
  FileWarning
} from 'lucide-react';
import { MermaidDiagram } from './MermaidDiagram';

interface NodeInfo {
  id: string;
  name: string;
  shortName: string;
  location: string;
  role: string;
  badge: string;
}

interface ChannelInfo {
  id: string;
  title: string;
  description: string;
  type: string;
  allowedNodes: string[];
  messageCount?: number;
  lastMessage?: any;
}

interface MessageItem {
  id: string;
  channelId: string;
  senderNodeId: string;
  senderName: string;
  text: string;
  timestamp: string;
  e2eeHash: string;
  attachments?: { name: string; size: string; type: string }[];
}

interface NcuvkzFinMessengerViewProps {
  onBack?: () => void;
  currentUserEmail?: string;
}

export const NcuvkzFinMessengerView: React.FC<NcuvkzFinMessengerViewProps> = ({
  onBack,
  currentUserEmail
}) => {
  const [nodes, setNodes] = useState<NodeInfo[]>([]);
  const [channels, setChannels] = useState<ChannelInfo[]>([]);
  const [activeChannelId, setActiveChannelId] = useState<string>('kolegiya');
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [inputText, setInputText] = useState<string>('');
  const [activeNodeId, setActiveNodeId] = useState<string>('center');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);

  // Стан панелі ШІ-аудиту
  const [auditDocText, setAuditDocText] = useState<string>(
    'Довідка про зміни до кошторису на листопад 2026 р.\nКПКВК: 2408010\nЗменшити видатки:\n- КЕКВ 2210 (Предмети, матеріали): -45 000,00 грн\nЗбільшити видатки:\n- КЕКВ 2240 (Оплата послуг крім комунальних - метрологія датчиків): +30 000,00 грн\n- КЕКВ 2273 (Оплата електроенергії сейсмопостів): +15 000,00 грн\nРазом зміна: 0,00 грн (Баланс збережено).'
  );
  const [auditResult, setAuditResult] = useState<any>(null);
  const [isAuditing, setIsAuditing] = useState<boolean>(false);
  const [auditLogsCount, setAuditLogsCount] = useState<number>(0);
  const [downloadSuccess, setDownloadSuccess] = useState<boolean>(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Завантаження базових даних
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [nodesRes, channelsRes] = await Promise.all([
        fetch('/api/ncuvkz-fin/nodes').then((r) => r.json()),
        fetch('/api/ncuvkz-fin/channels').then((r) => r.json())
      ]);
      if (nodesRes.ok) setNodes(nodesRes.nodes);
      if (channelsRes.ok) setChannels(channelsRes.channels);
    } catch (err) {
      console.error('Помилка завантаження конфігурації НЦУВКЗ:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Завантаження повідомлень активного каналу
  const fetchMessages = async (channelId: string) => {
    try {
      const res = await fetch(`/api/ncuvkz-fin/messages?channelId=${channelId}`);
      const data = await res.json();
      if (data.ok) {
        setMessages(data.messages);
      }
    } catch (err) {
      console.error('Помилка завантаження повідомлень:', err);
    }
  };

  // Завантаження кількості записів аудиту ДАСУ
  const fetchAuditLogsCount = async () => {
    try {
      const res = await fetch('/api/ncuvkz-fin/audit-log');
      const data = await res.json();
      if (data.ok) {
        setAuditLogsCount(data.count || (data.logs ? data.logs.length : 0));
      }
    } catch {}
  };

  useEffect(() => {
    fetchData();
    fetchAuditLogsCount();
  }, []);

  useEffect(() => {
    fetchMessages(activeChannelId);
  }, [activeChannelId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Відправка повідомлення
  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!inputText.trim()) return;

    setIsSending(true);
    const currentNode = nodes.find((n) => n.id === activeNodeId);
    const senderName =
      activeNodeId === 'center'
        ? 'Банах А.С. (Головний Бухгалтер НЦУВКЗ)'
        : `Головбух ${currentNode?.shortName || activeNodeId}`;

    try {
      const res = await fetch('/api/ncuvkz-fin/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channelId: activeChannelId,
          senderNodeId: activeNodeId,
          senderName,
          text: inputText.trim(),
          attachments: []
        })
      });
      const data = await res.json();
      if (data.ok) {
        setInputText('');
        fetchMessages(activeChannelId);
        fetchAuditLogsCount();
      }
    } catch (err) {
      console.error('Помилка надсилання повідомлення:', err);
    } finally {
      setIsSending(false);
    }
  };

  // Запуск ШІ-аудиту
  const handleRunAiAudit = async () => {
    if (!auditDocText.trim()) return;
    setIsAuditing(true);
    try {
      const currentNode = nodes.find((n) => n.id === activeNodeId);
      const res = await fetch('/api/ncuvkz-fin/ai-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          docText: auditDocText,
          docType: 'Довідка про зміни до кошторису',
          nodeContext: currentNode?.name || 'Філія НЦУВКЗ'
        })
      });
      const data = await res.json();
      if (data.ok) {
        setAuditResult(data.auditResult);
        fetchAuditLogsCount();
      }
    } catch (err) {
      console.error('Помилка запуску аудиту:', err);
    } finally {
      setIsAuditing(false);
    }
  };

  // Завантаження журналу аудиту ДАСУ
  const handleDownloadAuditLog = async () => {
    try {
      const res = await fetch('/api/ncuvkz-fin/audit-log');
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json;charset=utf-8'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `NCUVKZ_FIN_AUDIT_LOG_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 2500);
    } catch (err) {
      console.error('Помилка завантаження логів:', err);
    }
  };

  const activeChannel = channels.find((c) => c.id === activeChannelId);

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] bg-zinc-950 text-zinc-100 font-sans overflow-hidden">
      {/* 1. Верхня панель статусу та навігації */}
      <header className="flex flex-wrap items-center justify-between px-4 py-2.5 bg-zinc-900 border-b border-zinc-800 gap-3 z-10">
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="p-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors cursor-pointer"
              title="Повернутися до головного хабу"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <div className="flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-violet-600/20 text-violet-400 border border-violet-500/30">
              <Building2 className="w-5 h-5" />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-sm sm:text-base font-bold text-white tracking-wide">
                  НЦУВКЗ-ФІНКОНТУР
                </h1>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  E2EE On-Premise
                </span>
              </div>
              <p className="text-[11px] text-zinc-400 hidden sm:block">
                Закритий комунікатор головних бухгалтерів Центру (Київ) та 6 філій
              </p>
            </div>
          </div>
        </div>

        {/* Перемикач активного АРМ / Вузла для тестування та роботи */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 bg-zinc-950 px-2.5 py-1 rounded-lg border border-zinc-800 text-xs">
            <span className="text-[11px] text-zinc-400">Активний АРМ:</span>
            <select
              value={activeNodeId}
              onChange={(e) => setActiveNodeId(e.target.value)}
              className="bg-transparent text-violet-300 font-semibold focus:outline-none cursor-pointer text-xs"
            >
              {nodes.map((n) => (
                <option key={n.id} value={n.id} className="bg-zinc-900 text-zinc-100">
                  {n.badge} — {n.shortName}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleDownloadAuditLog}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 transition-colors"
            title="Завантажити незмінний журнал аудиту для перевірок ДАСУ"
          >
            {downloadSuccess ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-emerald-400">Лог завантажено</span>
              </>
            ) : (
              <>
                <Download className="w-3.5 h-3.5 text-zinc-400" />
                <span>Журнал ДАСУ ({auditLogsCount})</span>
              </>
            )}
          </button>
        </div>
      </header>

      {/* 2. Основний робочий простір (3 колонки) */}
      <div className="flex-1 flex overflow-hidden">
        {/* ЛІВА КОЛОНКА: Список Каналів та Вузлів НЦУВКЗ */}
        <aside className="w-64 sm:w-72 bg-zinc-900/70 border-r border-zinc-800 flex flex-col shrink-0">
          <div className="p-3 border-b border-zinc-800/80 bg-zinc-900/90 flex items-center justify-between">
            <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-violet-400" />
              Канали Бухгалтерії (8)
            </span>
            <button
              onClick={fetchData}
              className="p-1 text-zinc-400 hover:text-zinc-200 transition-colors"
              title="Оновити список"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-zinc-800/40 p-2 space-y-1">
            {channels.map((ch) => {
              const isActive = ch.id === activeChannelId;
              return (
                <button
                  key={ch.id}
                  onClick={() => setActiveChannelId(ch.id)}
                  className={`w-full text-left p-2.5 rounded-xl transition-all cursor-pointer flex flex-col gap-1 ${
                    isActive
                      ? 'bg-violet-950/60 border border-violet-500/40 text-white shadow-sm'
                      : 'hover:bg-zinc-800/60 text-zinc-300 border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-xs truncate flex items-center gap-1.5">
                      {ch.type === 'broadcast' ? '📢' : ch.type === 'audit' ? '⚡' : '🔒'}
                      {ch.title}
                    </span>
                    {ch.messageCount !== undefined && ch.messageCount > 0 && (
                      <span className="px-1.5 py-0.2 rounded-full text-[10px] font-mono bg-zinc-800 text-zinc-300">
                        {ch.messageCount}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-zinc-400 line-clamp-1">
                    {ch.description}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Інформаційна плашка про 7 вузлів */}
          <div className="p-3 bg-zinc-950 border-t border-zinc-800 text-[11px] text-zinc-400">
            <div className="font-semibold text-zinc-300 mb-1 flex items-center gap-1">
              <Building2 className="w-3.5 h-3.5 text-emerald-400" />
              Вузли контуру (7 підрозділів):
            </div>
            <div className="grid grid-cols-2 gap-1 text-[10px] text-zinc-400">
              <span>• Київ (Центр)</span>
              <span>• Дунаївці (ЦПОСІ)</span>
              <span>• Малин (ГЦСК)</span>
              <span>• Ластівці (РЦСК)</span>
              <span>• Мукачево/Золочів</span>
              <span>• Дніпрокосмос</span>
              <span className="col-span-2">• ОПРЦ «Мрія»</span>
            </div>
          </div>
        </aside>

        {/* ЦЕНТРАЛЬНА КОЛОНКА: Стрічка повідомлень та чат */}
        <main className="flex-1 flex flex-col bg-zinc-950 min-w-0">
          {/* Заголовок активного каналу */}
          <div className="px-4 py-2.5 bg-zinc-900/60 border-b border-zinc-800 flex items-center justify-between">
            <div className="min-w-0">
              <h2 className="text-xs sm:text-sm font-bold text-white flex items-center gap-2 truncate">
                <Lock className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                {activeChannel?.title}
              </h2>
              <p className="text-[11px] text-zinc-400 truncate">
                {activeChannel?.description}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0 text-[11px] bg-emerald-950/30 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-md">
              <Shield className="w-3 h-3" />
              <span>Шифрування TLS 1.3 + WebCrypto</span>
            </div>
          </div>

          {/* Список повідомлень */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3.5">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-zinc-500 text-xs">
                <Shield className="w-8 h-8 mb-2 text-zinc-600 stroke-[1.5]" />
                <p>У цьому захищеному каналі ще немає повідомлень.</p>
                <p className="text-[11px] text-zinc-600 mt-1">
                  Надішліть перше повідомлення або довідку змін до кошторису.
                </p>
              </div>
            ) : (
              messages.map((msg) => {
                const isMe = msg.senderNodeId === activeNodeId;
                const isCenter = msg.senderNodeId === 'center';
                return (
                  <div
                    key={msg.id}
                    className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} max-w-2xl ${
                      isMe ? 'ml-auto' : 'mr-auto'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-1 px-1 text-[11px] text-zinc-400">
                      <span className="font-semibold text-zinc-200">{msg.senderName}</span>
                      <span
                        className={`px-1.5 py-0.2 rounded text-[9px] font-mono ${
                          isCenter
                            ? 'bg-violet-900/60 text-violet-300'
                            : 'bg-zinc-800 text-zinc-300'
                        }`}
                      >
                        {msg.senderNodeId.toUpperCase()}
                      </span>
                      <span className="text-zinc-500">
                        {new Date(msg.timestamp).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>

                    <div
                      className={`p-3 rounded-2xl text-xs sm:text-[13px] leading-relaxed shadow-sm border ${
                        isMe
                          ? 'bg-violet-600 text-white border-violet-500/50 rounded-tr-none'
                          : 'bg-zinc-900 text-zinc-200 border-zinc-800 rounded-tl-none'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{msg.text}</p>

                      {/* Вкладені файли */}
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="mt-2.5 pt-2 border-t border-white/10 space-y-1.5">
                          {msg.attachments.map((att, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between gap-2 p-2 rounded-lg bg-black/20 border border-white/10 text-xs"
                            >
                              <div className="flex items-center gap-2 truncate">
                                <FileSpreadsheet className="w-4 h-4 text-emerald-300 shrink-0" />
                                <span className="font-mono truncate">{att.name}</span>
                                <span className="text-[10px] text-zinc-300 shrink-0">
                                  ({att.size})
                                </span>
                              </div>
                              <button
                                onClick={() => {
                                  setAuditDocText(
                                    `Документ: ${att.name}\nВідправник: ${msg.senderName}\nВміст: Проєкт фінансової довідки за КЕКВ-2240, 2273 на суму 45 000 грн.`
                                  );
                                  handleRunAiAudit();
                                }}
                                className="px-2 py-0.5 rounded bg-violet-900/80 hover:bg-violet-800 text-violet-200 text-[10px] font-semibold shrink-0 cursor-pointer transition-colors"
                              >
                                ⚡ ШІ-Аудит
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Хеш цілісності */}
                      <div className="mt-1.5 text-[9px] font-mono opacity-60 text-right">
                        SHA-256: {msg.e2eeHash}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Поле введення повідомлення */}
          <form
            onSubmit={handleSendMessage}
            className="p-3 bg-zinc-900/80 border-t border-zinc-800 flex items-end gap-2"
          >
            <div className="flex-1 bg-zinc-950 border border-zinc-800 rounded-xl p-2 focus-within:border-violet-500 transition-colors">
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder={`Написати в ${activeChannel?.title || 'чат'}... (Enter — надіслати)`}
                rows={2}
                className="w-full bg-transparent text-xs sm:text-[13px] text-zinc-100 placeholder-zinc-500 focus:outline-none resize-none leading-relaxed"
              />
              <div className="flex items-center justify-between pt-1 border-t border-zinc-800/40 text-[11px] text-zinc-400">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setInputText(
                        'Прошу погодити довідку змін до кошторису за КПКВК 2408010 на суму 25 000 грн (перерозподіл з КЕКВ-2210 на КЕКВ-2240).'
                      )
                    }
                    className="text-[10px] hover:text-violet-300 transition-colors cursor-pointer"
                  >
                    + Шаблон довідки КЕКВ
                  </button>
                </div>
                <span className="text-[10px] text-zinc-500">
                  Ви підписані як: <strong className="text-zinc-300">{activeNodeId.toUpperCase()}</strong>
                </span>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSending || !inputText.trim()}
              className="p-3 rounded-xl bg-violet-600 hover:bg-violet-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0 cursor-pointer shadow-md"
              title="Надіслати захищене повідомлення"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </main>

        {/* ПРАВА КОЛОНКА: ШІ-Аудитор Кошторисів та КЕКВ */}
        <aside className="w-80 sm:w-96 bg-zinc-900/60 border-l border-zinc-800 flex flex-col shrink-0 overflow-y-auto p-4 space-y-4">
          <div className="flex items-center justify-between pb-2 border-b border-zinc-800">
            <span className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
              <FileCheck2 className="w-4 h-4 text-emerald-400" />
              ШІ-Аудитор Кошторисів (КЕКВ)
            </span>
            <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-violet-500/20 text-violet-300 border border-violet-500/30">
              НКАУ / ДКСУ
            </span>
          </div>

          {/* Поле швидкого аналізу документа */}
          <div className="space-y-1.5">
            <label className="text-[11px] font-medium text-zinc-300">
              Текст довідки про зміни або реквізити:
            </label>
            <textarea
              value={auditDocText}
              onChange={(e) => setAuditDocText(e.target.value)}
              rows={5}
              className="w-full p-2.5 rounded-xl bg-zinc-950 border border-zinc-800 text-xs font-mono text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-violet-500 leading-relaxed"
              placeholder="Введіть статті КЕКВ, суми, код КПКВК..."
            />
            <button
              onClick={handleRunAiAudit}
              disabled={isAuditing || !auditDocText.trim()}
              className="w-full py-2 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition-all shadow-md cursor-pointer disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isAuditing ? 'animate-spin' : ''}`} />
              <span>{isAuditing ? 'Перевірка ДКСУ...' : '⚡ Запустити ШІ-Аудит КЕКВ'}</span>
            </button>
          </div>

          {/* Результат аудиту */}
          {auditResult && (
            <div className="space-y-3 p-3.5 rounded-xl bg-zinc-950 border border-zinc-800 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-bold text-white flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  Висновок Ревізора:
                </span>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  {auditResult.complianceScore || 98}% відповідність
                </span>
              </div>

              <p className="text-zinc-300 text-[11px] leading-relaxed">
                {auditResult.summary}
              </p>

              {/* Перевірені КЕКВ */}
              {auditResult.kekvChecked && auditResult.kekvChecked.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  <span className="text-[10px] text-zinc-400 mr-1">КЕКВ:</span>
                  {auditResult.kekvChecked.map((k: string, i: number) => (
                    <span
                      key={i}
                      className="px-1.5 py-0.2 rounded bg-zinc-800 text-violet-300 font-mono text-[10px]"
                    >
                      {k}
                    </span>
                  ))}
                </div>
              )}

              {/* Зауваження / Червоні прапорці */}
              {auditResult.redFlags && auditResult.redFlags.length > 0 && (
                <div className="p-2.5 rounded-lg bg-rose-950/30 border border-rose-500/30 text-rose-300 text-[11px] space-y-1">
                  <div className="font-semibold flex items-center gap-1 text-rose-400">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Зауваження для виправлення:
                  </div>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {auditResult.redFlags.map((rf: string, idx: number) => (
                      <li key={idx}>{rf}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Рекомендації */}
              {auditResult.recommendations && auditResult.recommendations.length > 0 && (
                <div className="space-y-1 text-[11px] text-zinc-400">
                  <span className="font-semibold text-zinc-300">Рекомендації:</span>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {auditResult.recommendations.map((rec: string, idx: number) => (
                      <li key={idx}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Діаграма Mermaid руху коштів */}
              {auditResult.mermaidDiagram && (
                <div className="mt-3 pt-3 border-t border-zinc-800">
                  <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider block mb-1">
                    Схема узгодження / Розподіл (Mermaid):
                  </span>
                  <MermaidDiagram chart={auditResult.mermaidDiagram} isDark={true} />
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default NcuvkzFinMessengerView;
